import * as React from "react";
import { Checkbox } from "@mui/material";
import List from "../list";
import Card from "../card";
import EditModal from "./glossary/edit-modal";
import RemoveModal from "./common/remove-modal";
import ListActions from "./list-actions/glossary";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useAuth } from "../../hooks/use-auth";
import { useAdminContext } from "../../hooks/use-admin-context";
import * as API from "../../services/api";

const messages = {
  deleteModal: "Supprimer une définition",
  deleteConfirm: "Etes-vous sur de vouloir supprimer cette définition ?",
  deleteSuccess: "Définition supprimée",
};

export default function Glossary({ Icon }) {
  const [definitions, setDefinitions] = React.useState([]);
  const [ready, setReady] = React.useState(false);
  const classes = styles();
  const { token } = useAuth();
  const { openModal, triggerError, triggerSuccess, setLoader } =
    useAdminContext();

  React.useEffect(() => {
    const get = async () => {
      const result = await API.getDefinitions(token);
      setDefinitions(result);
      setReady(true);
    };
    get();
    return () => setDefinitions(undefined);
  }, [token]);

  const update = async ({ id, word, definition, kind, enabled }, checked) => {
    setLoader(true);
    const newDefinitions = await API.updateDefinition(
      token,
      id,
      word,
      definition,
      kind,
      checked ?? enabled
    );
    setDefinitions(newDefinitions);
    triggerSuccess("Définition mise à jour avec succès");
    setLoader(false);
  };

  const remove = (id) => async () => {
    const newDefinitions = await API.removeDefinition(token, id);
    setDefinitions(newDefinitions);
    return true;
  };

  const onRemoveButtonClick = (id) =>
    openModal(<RemoveModal remove={remove(id)} messages={messages} />);
  const onWriteButtonClick = (id) => {
    const definition = definitions.find((definition) => definition.id === id);
    openModal(
      <EditModal
        add={definition}
        callback={update}
        title="Éditer une définition"
      />
    );
  };

  const renderCell = (row) => {
    const enabled = Boolean(row.enabled);
    const onChange = (_, checked) => update(row, checked);
    return (
      <Checkbox
        sx={classes.checkbox}
        checked={enabled}
        onChange={onChange}
        color="primary"
      />
    );
  };

  const addDefinition = async (add) => {
    setLoader(true);
    const { word, definition, kind, enabled } = add;
    if (!word) {
      setLoader(false);
      return triggerError("Veuillez ajouter un mot.");
    }
    if (!definition) {
      setLoader(false);
      return triggerError("Veuillez ajouter une définition.");
    }
    const newDefinitions = await API.createDefinition(
      token,
      word,
      definition,
      kind,
      enabled
    );
    setDefinitions(newDefinitions);
    triggerSuccess(`Nouvelle définition ajoutée.`);
    setLoader(false);
  };

  const columns = [
    { field: "id", hidden: true, headerName: "id" },
    { field: "word", headerName: "Mot", width: 150 },
    { field: "definition", headerName: "Définition" },
    { field: "kind", headerName: "Type" },
    { field: "enabled", headerName: "Visible", width: 73, renderCell },
  ];

  const rowActions = [
    { Icon: EditIcon, callback: onWriteButtonClick },
    { Icon: DeleteIcon, callback: onRemoveButtonClick },
  ];

  const actionsProps = { definitions, callback: update, addDefinition };

  return (
    <Card
      Icon={Icon}
      title="Gestion des définitions"
      ListActions={ListActions}
      actionsProps={actionsProps}
    >
      <List
        width="100%"
        loading={definitions === undefined}
        columns={columns}
        rows={definitions}
        rowActions={rowActions}
        ready={ready}
      />
    </Card>
  );
}

function styles() {
  return {
    checkbox: {
      justifySelf: "center",
    },
  };
}
